/**
 * @description Transaction details, including offline method/reference edits.
 * @dependencies transactionsService, paymentMethodDisplay
 * @notes PUT /transactions/:id wraps { success, data }. Save refetches GET so
 * amount and invoicePaymentMethod stay current. Do not setState from the wrapper.
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/AuthContext';
import { transactionsService, refundsService } from '../services/api';
import voidService from '../services/api/voidService';
import { formatCurrency } from '../utils/invoiceHelpers';
import { formatPaymentMethodLabel, resolveEditablePaymentMethod } from '../utils/paymentMethodDisplay';
import { getCustomerDisplayName } from '../utils/customerHelpers';
import StatusBadge from './shared/StatusBadge';
import RefundModal from './shared/RefundModal';
import VoidModal from './shared/VoidModal';
import GatewayErrorAlert from './shared/GatewayErrorAlert';
import PermissionGate from './shared/PermissionGate';
import { PERMISSIONS } from '../constants/permissionMappings';

function TransactionDetails() {
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('classification');
  
  // External payment editing state
  const [isEditingExternal, setIsEditingExternal] = useState(false);
  const [externalData, setExternalData] = useState({
    referenceNumber: '',
    paymentMethod: ''
  });
  
  // Refund state
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [processingRefund, setProcessingRefund] = useState(false);
  const [refundError, setRefundError] = useState('');
  
  // Void state
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [voidEligibility, setVoidEligibility] = useState(null);
  const [gatewayError, setGatewayError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const { transactionId } = useParams();
  const { user } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    // Redirect if transactionId is undefined or invalid
    if (!transactionId || transactionId === 'undefined') {
      console.warn('Invalid transaction ID:', transactionId);
      navigate(-1);
      return;
    }
    
    const fetchTransactionDetails = async () => {
      try {
        setLoading(true);
        
        // Fetch transaction details - no auth header needed
        const response = await transactionsService.getTransaction(transactionId);
        console.log('Transaction API response:', response);
        
        if (response) {
          setTransaction(response); // GET returns the DTO; Who comes from customer_id JOIN
          setError(null);
          
          // Check void eligibility if transaction loaded successfully
          if (response) {
            try {
              const eligibility = await voidService.checkVoidEligibility(response.id || transactionId);
              setVoidEligibility(eligibility);
            } catch (voidErr) {
              console.error('Error checking void eligibility:', voidErr);
              // Non-critical error, don't show to user
            }
          }
        } else {
          setError('Transaction not found');
        }
      } catch (err) {
        console.error('Error fetching transaction details:', err);
        setError('Failed to load transaction details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (user?.id) {
      fetchTransactionDetails();
    }
  }, [transactionId, navigate, user?.id]);

  // Who SoT: transactions.customer_id → customers JOIN on the DTO.
  // Display: company first, then first_name last_name. Guest only when customer_id is null.
  const companyName = (transaction?.customerCompany || '').trim() || null;
  const individualName = `${transaction?.customerFirstName || ''} ${transaction?.customerLastName || ''}`.trim() || null;
  const hasLinkedCustomer = Boolean(transaction?.customerId);
  const hasCompany = !!companyName;
  const hasIndividual = !!individualName;

  // External payment editing functions
  const handleEditExternal = () => {
    setExternalData({
      referenceNumber: transaction.externalReference || '',
      paymentMethod: resolveEditablePaymentMethod(transaction)
    });
    setIsEditingExternal(true);
  };

  const handleCancelExternalEdit = () => {
    setIsEditingExternal(false);
    setExternalData({
      referenceNumber: '',
      paymentMethod: ''
    });
  };

  const handleSaveExternal = async () => {
    try {
      const updateData = {
        externalReference: externalData.referenceNumber,
        paymentMethod: externalData.paymentMethod
      };

      await transactionsService.updateTransaction(transaction.id, updateData);
      // Refetch GET-shaped details (linked invoices + invoicePaymentMethod rail).
      const refreshed = await transactionsService.getTransaction(transactionId);
      setTransaction(refreshed);
      setIsEditingExternal(false);
    } catch (error) {
      console.error('Error updating external payment details:', error);
      setError('Failed to update external payment details');
    }
  };

  // Helper function to detect transaction type and get metadata
  const getTransactionTypeInfo = () => {
    if (!transaction) {
      return {
        type: 'unknown',
        displayName: 'Loading...',
        description: 'Loading transaction details',
        hasFullCardData: false,
        hasAvsData: false,
        hasCustomerData: false,
        showGatewayInfo: false,
        icon: '⏳'
      };
    }
    
    const ref = transaction.transactionReference || transaction.transactionId || '';
    const channel = transaction.channel || '';
    
    if (ref.startsWith('EXT-') || channel === 'external' || 
        (channel === 'merchant_portal' && (transaction.paymentMethod === 'check' || 
         transaction.paymentMethod === 'cash' || transaction.paymentMethod === 'bank_transfer' ||
         transaction.paymentMethod === 'wire' || transaction.paymentMethod === 'external' ||
         transaction.paymentMethod === 'other'))) {
      return {
        type: 'external',
        displayName: 'External Payment',
        description: 'Payment recorded externally and logged in system',
        hasFullCardData: false,
        hasAvsData: false,
        hasCustomerData: false,
        showExternalPaymentInfo: true,
        icon: '📝'
      };
    }
    
    if (ref.startsWith('Portal-') || channel === 'customer_portal') {
      return {
        type: 'portal',
        displayName: 'Customer Portal Payment',
        description: 'Payment made through customer portal',
        hasFullCardData: true,
        hasAvsData: !!(transaction.avsResponse || transaction.cvvResponse),
        hasCustomerData: true,
        showExternalPaymentInfo: false,
        icon: '🌐'
      };
    }

    // Default to merchant dashboard payment
    return {
      type: 'merchant',
      displayName: 'Merchant Dashboard Payment',
      description: 'Payment processed through merchant dashboard',
      hasFullCardData: true,
      hasAvsData: !!(transaction.avsResponse || transaction.cvvResponse),
      hasCustomerData: true,
      showExternalPaymentInfo: false,
      icon: '💳'
    };
  };

  const transactionTypeInfo = getTransactionTypeInfo();

  // Void eligibility check (uses backend validation)
  const canVoid = (transaction) => {
    return transaction && voidEligibility?.eligible === true;
  };

  // Transaction eligibility for refund — must be a gateway-processed card payment
  const canRefund = (transaction) => {
    if (!transaction) return false;

    // Don't show refund if void is still available (pre-settlement approximation)
    if (voidEligibility?.eligible) return false;

    // Gateway-processed card transaction: has a gateway transaction ID or is payment_method 'card'
    const isGatewayCardPayment = !!(transaction.gatewayTransactionId || transaction.gateway_transaction_id) ||
                                 transaction.paymentMethod === 'card' || transaction.payment_method === 'card';

    const isApprovedAndPaid = (transaction.status === 'APPROVAL' || transaction.status === 'partially_refunded') &&
                             (transaction.paymentStatus === 'paid' || transaction.payment_status === 'paid');

    const isNotExternal = transaction.channel !== 'external';

    const isPurchaseTransaction = transaction.transactionType === 'purchase' ||
                                 transaction.transaction_type === 'purchase' ||
                                 (!transaction.transactionType && !transaction.transaction_type);

    const hasRefundableBalance = (transaction.amount || 0) > (transaction.refundedAmount || transaction.refunded_amount || 0);

    return isGatewayCardPayment && isApprovedAndPaid && isNotExternal && isPurchaseTransaction && hasRefundableBalance;
  };

  // Handle refund actions
  const handleRefundClick = () => {
    setShowRefundModal(true);
  };

  // Handle void actions
  const handleVoidClick = () => {
    setShowVoidModal(true);
  };

  const handleRefundConfirm = async (refundData) => {
    try {
      setProcessingRefund(true);
      setRefundError('');
      
      await refundsService.createRefund({
        transactionId: transaction.transactionReference || transaction.transaction_reference || transaction.reference || transaction.id,
        amount: refundData.amount,
        reason: refundData.reason,
        notes: refundData.notes
      });
      
      setSuccessMessage(`Refund of ${formatCurrency(refundData.amount)} processed successfully`);
      setShowRefundModal(false);
      
      // Refresh transaction data
      const updatedTransaction = await transactionsService.getTransaction(transactionId);
      setTransaction(updatedTransaction);
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error) {
      console.error('Refund failed:', error);
      const errorMessage = error.response?.data?.error || error.message || 'An error occurred while processing the refund';
      
      // Check if this is a gateway configuration error
      if (errorMessage.includes('Gateway not connected') || 
          error.response?.status === 503 ||
          error.response?.data?.code === 'GATEWAY_NOT_CONFIGURED') {
        setGatewayError(errorMessage);
        setShowRefundModal(false);
      } else {
        setRefundError(errorMessage);
      }
    } finally {
      setProcessingRefund(false);
    }
  };

  // Helper functions for AVS and CVV status
  const getAvsResponseText = (code) => {
    switch (code) {
      case 'Y': return 'Address and ZIP match';
      case 'A': return 'Address matches, ZIP does not';
      case 'Z': return 'ZIP matches, Address does not';
      case 'N': return 'Neither ZIP nor Address match';
      case 'U': return 'Address info unavailable';
      case 'R': return 'System unavailable, retry';
      case 'E': return 'Not eligible for AVS';
      case 'S': return 'Not supported';
      case 'D': return 'International match';
      case 'M': return 'Street and Postal Code match';
      case 'B': return 'Address matches (Street only)';
      case 'P': return 'Postal Code matches';
      default: return 'Not Available';
    }
  };

  // AVS status handling moved to StatusBadge component

  const getCvvResponseText = (code) => {
    switch (code) {
      case 'M': return 'CVV matches';
      case 'N': return 'CVV does not match';
      case 'P': return 'Not processed';
      case 'S': return 'CVV should be on card';
      case 'U': return 'Issuer does not support';
      case 'I': return 'Invalid';
      default: return 'Not Available';
    }
  };

  // CVV status handling moved to StatusBadge component

  if (loading) {
    return (
      <div className="flex justify-center items-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 p-4 rounded-lg">
          <p className="text-red-600">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
          >
            Back to Transactions
          </button>
        </div>
      </div>
    );
  }

  if (!transaction) {
    return (
      <div className="p-6">
        <div className="mt-6">
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
          >
            Back to Transactions
          </button>
        </div>
      </div>
    );
  }

  // Get transaction reference with proper fallback order
  const txId = transaction.transactionReference || 
              transaction.id || 
              'N/A';

  // Get external reference if available
  const hasExternalRef = transaction.externalReference && transaction.gatewayName;

  // Get card last four with fallbacks (industry standard: last4)
  const cardLastFour = transaction.last4 || 
                     transaction.cardLastFour || 
                     transaction.card_last_four ||
                     (transaction.cardNumber ? transaction.cardNumber.slice(-4) : '1111');

  // Get company name
  // companyName is now defined above in the customer data extraction

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6">
      {/* Back Navigation */}
      <div className="flex items-center">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          <span className="text-sm">Back to Transactions</span>
        </button>
      </div>

      {/* Page Title and Status */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="space-y-2">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Transaction Details</h2>
          {transaction.transactionType !== 'refund' && (
            <div className="flex items-center gap-2">
              <span className="text-lg">{transactionTypeInfo.icon}</span>
              <div className="text-sm text-gray-600">
                <span className="font-medium">{transactionTypeInfo.displayName}</span>
                <span className="mx-2">•</span>
                <span>{transactionTypeInfo.description}</span>
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge 
            type="transaction_status"
            value={{
              status: transaction.status,
              transactionType: transaction.transactionType,
              isVoided: transaction.isVoided || !!transaction.voidedAt
            }}
            size="md"
          />
          <PermissionGate permission={PERMISSIONS.payments.refund}>
            {canRefund(transaction) && (
              <button
                onClick={handleRefundClick}
                disabled={processingRefund}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-gray-200 hover:border-orange-200"
                title="Refund transaction"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                </svg>
                {processingRefund ? 'Processing...' : 'Refund'}
              </button>
            )}
          </PermissionGate>
          <PermissionGate permission={PERMISSIONS.payments.void}>
            {canVoid(transaction) && (
              <button
                onClick={handleVoidClick}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-gray-200 hover:border-red-200"
                title={voidEligibility?.reason || "Void transaction"}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
                Void
              </button>
            )}
          </PermissionGate>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {/* Customer Info Header */}
        <div className="bg-gradient-to-r from-gray-50 via-gray-50 to-gray-100 px-6 md:px-8 py-6 border-b border-gray-200">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="space-y-1">
              {hasCompany && (
                <h3 className="text-xl font-semibold text-gray-900">{companyName}</h3>
              )}
              {hasIndividual && (
                <p className={`text-base ${hasCompany ? 'text-gray-600' : 'text-gray-900 font-semibold'}`}>
                  {individualName}
                </p>
              )}
              {!hasCompany && !hasIndividual && !hasLinkedCustomer && (
                <p className="text-base text-gray-600">Guest</p>
              )}
            </div>
            <div className="flex flex-col items-end gap-1">
              <div className="text-2xl font-bold text-gray-900">
                {formatCurrency(transaction.amount)}
              </div>
              {transaction.surchargeAmountCents > 0 && (
                <div className="text-right space-y-0.5">
                  <div className="text-xs text-amber-600">
                    +{formatCurrency(transaction.surchargeAmount)} surcharge
                  </div>
                  <div className="text-sm font-semibold text-gray-900">
                    {formatCurrency(transaction.totalChargedAmount)} total charged
                  </div>
                </div>
              )}
              <div className="text-sm text-gray-500">
                {transaction.date ?
                  new Date(transaction.date).toLocaleString() :
                  (transaction.createdAt ?
                    new Date(transaction.createdAt).toLocaleString() :
                    'N/A')}
              </div>
            </div>
          </div>
        </div>

        {/* Transaction Details Grid */}
        <div className="p-6 md:p-8 space-y-6">
          {/* First Row - Payment Details and Transaction Information (for external) OR Payment Details and Billing Details (for internal) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Payment Details - Conditional rendering based on transaction type */}
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-sm font-semibold text-gray-900">
                  {transaction.transactionType === 'refund' ? 'Refund Details' : 
                   transactionTypeInfo.showExternalPaymentInfo ? 'External Payment Details' : 'Payment Details'}
                </h4>
                {transactionTypeInfo.showExternalPaymentInfo && !isEditingExternal && (
                  <button
                    onClick={handleEditExternal}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Edit
                  </button>
                )}
              </div>
              <div className="space-y-4">
                {/* Show external payment info for external transactions */}
                {transactionTypeInfo.showExternalPaymentInfo && (
                  <>
                    {isEditingExternal ? (
                      <>
                        {/* Editing Mode */}
                        <div>
                          <label className="block text-sm text-gray-500 mb-1">Reference Number</label>
                          <input
                            type="text"
                            value={externalData.referenceNumber}
                            onChange={(e) => setExternalData(prev => ({ ...prev, referenceNumber: e.target.value }))}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-brand focus:border-transparent"
                            placeholder="Check number, transaction ID, etc."
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm text-gray-500 mb-1">Payment Method</label>
                          <select
                            value={externalData.paymentMethod}
                            onChange={(e) => setExternalData(prev => ({ ...prev, paymentMethod: e.target.value }))}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-brand focus:border-transparent"
                          >
                            <option value="">Select method</option>
                            <option value="cash">Cash</option>
                            <option value="check">Check</option>
                            <option value="wire">Bank Transfer</option>
                            <option value="credit_card">Credit Card (already collected)</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                        
                        <div className="flex gap-2 pt-2">
                          <button
                            onClick={handleSaveExternal}
                            className="px-3 py-1.5 bg-brand text-white text-xs rounded-md hover:bg-brand-hover transition-colors"
                          >
                            Save
                          </button>
                          <button
                            onClick={handleCancelExternalEdit}
                            className="px-3 py-1.5 bg-gray-200 text-gray-700 text-xs rounded-md hover:bg-gray-300 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Display Mode */}
                        {/* Reference Number - First for external payments */}
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Reference Number</p>
                          <p className="text-sm font-medium text-gray-900 font-mono">
                            {transaction.externalReference || 'Not specified'}
                          </p>
                        </div>
                        
                        {/* Payment Method */}
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Payment Method</p>
                          <p className="text-sm font-medium text-gray-900">
                            {formatPaymentMethodLabel(
                              transaction.paymentMethod,
                              transaction.invoicePaymentMethod
                            )}
                          </p>
                        </div>
                      </>
                    )}
                  </>
                )}
                
                {/* Currency - Always show */}
                <div>
                  <p className="text-sm text-gray-500 mb-1">Currency</p>
                  <p className="text-sm font-medium text-gray-900">{transaction.currency || 'USD'}</p>
                </div>
                
                {/* Show additional external payment info */}
                {transactionTypeInfo.showExternalPaymentInfo && (
                  <>
                    {/* Payment Date */}
                    {transaction.paymentDate && (
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Payment Date</p>
                        <p className="text-sm font-medium text-gray-900">
                          {new Date(transaction.paymentDate).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                    )}
                  </>
                )}
                
                {/* Show card details for internal transactions */}
                {transactionTypeInfo.hasFullCardData && (
                  <>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Card Type</p>
                      <div className="flex items-center space-x-3">
                        {(() => {
                          const cardType = (transaction.brand || transaction.cardType || transaction.card_type || '').toLowerCase();
                          if (cardType.includes('visa')) {
                            return (
                              <div className="flex items-center space-x-2">
                                <div className="w-8 h-6 bg-gradient-to-r from-blue-600 to-blue-800 rounded flex items-center justify-center">
                                  <span className="text-xs font-bold text-white">V</span>
                                </div>
                                <span className="text-sm font-medium text-gray-900">Visa</span>
                              </div>
                            );
                          } else if (cardType.includes('master')) {
                            return (
                              <div className="flex items-center space-x-2">
                                <div className="w-8 h-6 bg-gradient-to-r from-red-500 to-red-700 rounded flex items-center justify-center">
                                  <span className="text-xs font-bold text-white">M</span>
                                </div>
                                <span className="text-sm font-medium text-gray-900">Mastercard</span>
                              </div>
                            );
                          } else if (cardType.includes('amex') || cardType.includes('american')) {
                            return (
                              <div className="flex items-center space-x-2">
                                <div className="w-8 h-6 bg-gradient-to-r from-green-600 to-green-800 rounded flex items-center justify-center">
                                  <span className="text-xs font-bold text-white">A</span>
                                </div>
                                <span className="text-sm font-medium text-gray-900">American Express</span>
                              </div>
                            );
                          } else if (cardType.includes('discover')) {
                            return (
                              <div className="flex items-center space-x-2">
                                <div className="w-8 h-6 bg-gradient-to-r from-orange-500 to-orange-700 rounded flex items-center justify-center">
                                  <span className="text-xs font-bold text-white">D</span>
                                </div>
                                <span className="text-sm font-medium text-gray-900">Discover</span>
                              </div>
                            );
                          } else {
                            return (
                              <div className="flex items-center space-x-2">
                                <div className="w-8 h-6 bg-gradient-to-r from-gray-400 to-gray-600 rounded flex items-center justify-center">
                                  <span className="text-xs font-bold text-white">
                                  {(transaction.brand || transaction.cardType || transaction.card_type || '?').charAt(0).toUpperCase()}
                                  </span>
                                </div>
                                <span className="text-sm font-medium text-gray-900">{transaction.brand || transaction.cardType || transaction.card_type || 'Unknown'}</span>
                              </div>
                            );
                          }
                        })()}
                      </div>
                    </div>
                    {transaction.cardholderName && (
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Cardholder Name</p>
                        <p className="text-sm font-medium text-gray-900">{transaction.cardholderName}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Card Ending</p>
                      <p className="text-sm font-medium text-gray-900 font-mono">•••• {cardLastFour}</p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Transaction Information for External Payments OR Billing Details for Internal Payments */}
            {transactionTypeInfo.showExternalPaymentInfo ? (
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                <h4 className="text-sm font-semibold text-gray-900 mb-4">Transaction Information</h4>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Transaction ID</p>
                    <p className="text-sm font-medium text-gray-900 font-mono">{txId}</p>
                  </div>
                  {hasExternalRef && (
                    <div>
                      <p className="text-sm text-gray-500 mb-1">{transaction.gatewayName?.charAt(0).toUpperCase() + transaction.gatewayName?.slice(1)} Reference</p>
                      <p className="text-sm font-medium text-gray-900 font-mono">{transaction.externalReference}</p>
                    </div>
                  )}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-xs text-blue-800">
                        <span className="font-medium">External Payment</span><br />
                        This payment was processed outside the system and recorded for accounting purposes.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              transactionTypeInfo.hasCustomerData && (transaction.billingAddress || transaction.billingCity) && (
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                  <h4 className="text-sm font-semibold text-gray-900 mb-4">Billing Details</h4>
                  <div className="space-y-4">
                    {transaction.billingAddress && (
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Address</p>
                        <p className="text-sm font-medium text-gray-900">{transaction.billingAddress}</p>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-4">
                      {transaction.billingCity && (
                        <div>
                          <p className="text-sm text-gray-500 mb-1">City</p>
                          <p className="text-sm font-medium text-gray-900">{transaction.billingCity}</p>
                        </div>
                      )}
                      {transaction.billingState && (
                        <div>
                          <p className="text-sm text-gray-500 mb-1">State</p>
                          <p className="text-sm font-medium text-gray-900">{transaction.billingState}</p>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      {transaction.billingZip && (
                        <div>
                          <p className="text-sm text-gray-500 mb-1">ZIP Code</p>
                          <p className="text-sm font-medium text-gray-900">{transaction.billingZip}</p>
                        </div>
                      )}
                      {transaction.billingCountry && (
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Country</p>
                          <p className="text-sm font-medium text-gray-900">{transaction.billingCountry}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            )}
          </div>

          {/* Second Row - Transaction Information - Only show for internal transactions */}
          {!transactionTypeInfo.showExternalPaymentInfo && (
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
              <h4 className="text-sm font-semibold text-gray-900 mb-4">Transaction Information</h4>
              <div className={`grid gap-6 ${transactionTypeInfo.hasAvsData ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
                {/* Left Side - Always show */}
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Transaction ID</p>
                    <p className="text-sm font-medium text-gray-900 font-mono">{txId}</p>
                  </div>
                  {(transaction.gatewayTransactionId || transaction.gateway_transaction_id) && (
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Gateway ID</p>
                      <p className="text-sm font-medium text-gray-900 font-mono">{transaction.gatewayTransactionId || transaction.gateway_transaction_id}</p>
                    </div>
                  )}
                  {hasExternalRef && (
                    <div>
                      <p className="text-sm text-gray-500 mb-1">{transaction.gatewayName?.charAt(0).toUpperCase() + transaction.gatewayName?.slice(1)} Reference</p>
                      <p className="text-sm font-medium text-gray-900 font-mono">{transaction.externalReference}</p>
                    </div>
                  )}
                </div>

                {/* Right Side - Only show for internal transactions with AVS data */}
                {transactionTypeInfo.hasAvsData && (
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">AVS Response</p>
                      <div className="flex items-center space-x-2">
                        <StatusBadge 
                          type="avs_code" 
                          value={transaction.avsResponse}
                          size="xs"
                        />
                        {transaction.avsResponse && (
                          <span className="text-sm text-gray-600">
                            {getAvsResponseText(transaction.avsResponse)}
                          </span>
                        )}
                      </div>
                    </div>
                    {transaction.cvvResponse && (
                    <div>
                      <p className="text-sm text-gray-500 mb-1">CVV Response</p>
                      <div className="flex items-center space-x-2">
                        <StatusBadge
                          type="cvv_code"
                          value={transaction.cvvResponse}
                          size="xs"
                        />
                        <span className="text-sm text-gray-600">
                          {getCvvResponseText(transaction.cvvResponse)}
                        </span>
                      </div>
                    </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notes Section - Full Width */}
          <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
            <h4 className="text-sm font-semibold text-gray-900 mb-4">Transaction Notes</h4>
            <TransactionNotes transaction={transaction} />
          </div>

          {/* Tabbed Section - Transaction Classification & Linked Invoices */}
          <div className="bg-gray-50 rounded-xl border border-gray-100 overflow-hidden">
            {/* Tab Navigation */}
            <div className="bg-white border-b border-gray-200">
              <nav className="flex">
                <button
                  onClick={() => setActiveTab('classification')}
                  className={`flex-1 py-3 px-6 text-sm font-medium transition-colors ${
                    activeTab === 'classification'
                      ? 'border-b-2 border-brand text-gray-900 bg-brand-light'
                      : 'text-gray-500 hover:text-gray-700 border-b-2 border-transparent'
                  }`}
                >
                  Transaction Classification
                </button>
                <button
                  onClick={() => setActiveTab('linkedInvoices')}
                  className={`flex-1 py-3 px-6 text-sm font-medium transition-colors ${
                    activeTab === 'linkedInvoices'
                      ? 'border-b-2 border-brand text-gray-900 bg-brand-light'
                      : 'text-gray-500 hover:text-gray-700 border-b-2 border-transparent'
                  }`}
                >
                  Linked Invoices
                  {transaction.linkedInvoices && transaction.linkedInvoices.length > 0 && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-800">
                      {transaction.linkedInvoices.length}
                    </span>
                  )}
                </button>
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {/* Transaction Classification Tab */}
              {activeTab === 'classification' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Show refund reason for refunds, entry mode for regular transactions */}
                  {transaction.transactionType === 'refund' ? (
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                      <p className="text-sm text-gray-600">Reason</p>
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium border bg-orange-100 text-orange-800 border-orange-200">
                        {transaction.refundReason || 'No reason specified'}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                      <p className="text-sm text-gray-600">Entry Mode</p>
                      <StatusBadge type="entry_mode" value={transaction.entryMode} />
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-600">Channel</p>
                    <StatusBadge type="channel" value={transaction.channel} />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-600">Transaction Type</p>
                    <StatusBadge type="transaction_type" value={transaction.transactionType} />
                  </div>
                  
                  {/* Show card present only for non-refund transactions */}
                  {transaction.transactionType !== 'refund' && (
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                      <p className="text-sm text-gray-600">Card Present</p>
                      <StatusBadge 
                        type="card_present" 
                        value={transaction.cardPresent}
                        size="sm"
                      />
                    </div>
                  )}
                  {transaction.refundedAmount > 0 && (
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                      <p className="text-sm text-gray-600">Refunded Amount</p>
                      <span className="text-sm font-medium text-orange-600">
                        {formatCurrency(transaction.refundedAmount)} of {formatCurrency(transaction.originalAmount || transaction.amount)}
                      </span>
                    </div>
                  )}
                  {transaction.parentTransactionId && (
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 lg:col-span-3">
                      <p className="text-sm text-gray-600">Parent Transaction</p>
                      <a href={`/merchant/transactions/${transaction.parentTransactionId}`} className="text-sm font-medium text-brand hover:text-brand-hover underline">
                        View Original Transaction
                      </a>
                    </div>
                  )}
                </div>
              )}

              {/* Linked Invoices Tab */}
              {activeTab === 'linkedInvoices' && (
                <div>
                  {transaction.linkedInvoices && transaction.linkedInvoices.length > 0 ? (
                    <>
                      <div className="space-y-3">
                        {transaction.linkedInvoices.map((invoice, index) => (
                          <div key={invoice.id || index} className="bg-white rounded-lg border border-gray-200 p-4">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-3">
                                  <a 
                                    href={`/merchant/invoices/${invoice.invoiceNumber}`}
                                    className="text-sm font-semibold text-brand hover:text-brand-hover"
                                  >
                                    {invoice.invoiceNumber}
                                  </a>
                                  <StatusBadge 
                                    type="invoice_status" 
                                    value={{
                                      paymentStatus: invoice.paymentStatus,
                                      status: invoice.status,
                                      dueDate: invoice.dueDate
                                    }}
                                    size="xs"
                                  />
                                </div>
                                <p className="text-sm text-gray-600 mt-1">
                                  {getCustomerDisplayName(invoice)}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm text-gray-500">Allocated Amount</p>
                                <p className="text-base font-semibold text-gray-900">
                                  {formatCurrency(invoice.allocatedAmount || 0)}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  of {formatCurrency(invoice.totalAmount || 0)} total
                                </p>
                              </div>
                            </div>
                            {invoice.paymentDate && (
                              <div className="mt-2 pt-2 border-t border-gray-100">
                                <p className="text-xs text-gray-500">
                                  Payment recorded on {new Date(invoice.paymentDate).toLocaleDateString()}
                                </p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="flex justify-between items-center">
                          <p className="text-sm font-medium text-gray-600">Total Allocated</p>
                          <p className="text-base font-semibold text-gray-900">
                            {formatCurrency(transaction.linkedInvoices.reduce((sum, inv) => sum + parseFloat(inv.allocatedAmount || 0), 0))}
                          </p>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-12">
                      <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                      <p className="mt-4 text-sm font-medium text-gray-900">No Linked Invoices</p>
                      <p className="mt-2 text-sm text-gray-500">
                        This transaction is not linked to any invoices
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Success Message */}
      {successMessage && (
        <div className="mt-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
          {successMessage}
        </div>
      )}
      
      {/* Error Message */}
      {refundError && (
        <div className="mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded flex justify-between items-center">
          <span>{refundError}</span>
          <button 
            onClick={() => setRefundError('')}
            className="text-red-700 hover:text-red-900"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
      
      <RefundModal
        isOpen={showRefundModal}
        onClose={() => {
          setShowRefundModal(false);
          setRefundError('');
        }}
        onConfirm={handleRefundConfirm}
        transaction={transaction}
        processing={processingRefund}
        apiError={refundError}
      />
      
      <VoidModal
        isOpen={showVoidModal}
        onClose={() => {
          setShowVoidModal(false);
        }}
        onVoidSuccess={async (result) => {
          setSuccessMessage('Transaction voided successfully');
          // Refresh transaction data to show updated status
          try {
            const updatedTransaction = await transactionsService.getTransaction(transactionId);
            setTransaction(updatedTransaction);
            setVoidEligibility(null); // No longer eligible after voiding
          } catch (err) {
            console.error('Error refreshing transaction:', err);
          }
          // Clear success message after 5 seconds
          setTimeout(() => setSuccessMessage(''), 5000);
        }}
        transaction={transaction}
      />
      
      {/* Gateway Error Alert */}
      <GatewayErrorAlert
        error={gatewayError}
        onClose={() => setGatewayError('')}
      />
    </div>
  );
}

// TransactionNotes Component
function TransactionNotes({ transaction }) {
  const [notes, setNotes] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [originalNotes, setOriginalNotes] = useState('');

  useEffect(() => {
    // Clean architecture: Backend provides unified notes field
    // No complex logic needed - just use the notes field from API
    const displayNotes = transaction?.notes || '';
      
    setNotes(displayNotes);
    setOriginalNotes(displayNotes);
  }, [transaction]);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      
      // Clean architecture: Send notes field, let backend determine where to store
      const transactionId = transaction.transactionReference || transaction.id;
      await transactionsService.updateTransaction(transactionId, {
        notes: notes
      });
      
      // Update the original notes to the new saved value
      setOriginalNotes(notes);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving notes:', error);
      alert('Failed to save notes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setNotes(originalNotes);
    setIsEditing(false);
  };

  if (!transaction) {
    return (
      <div className="text-sm text-gray-500">
        Loading transaction notes...
      </div>
    );
  }

  return (
    <div>
      {isEditing ? (
        <div className="space-y-4">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-brand focus:border-brand text-sm"
            rows="4"
            placeholder={transaction?.transactionType === 'refund' 
              ? "Add notes about this refund..." 
              : "Add notes about this transaction..."}
          />
          <div className="flex justify-end gap-3">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-brand border border-transparent rounded-lg hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Save Notes'}
            </button>
          </div>
        </div>
      ) : (
        <div>
          {notes ? (
            <div className="space-y-3">
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{notes}</p>
              <button
                onClick={() => setIsEditing(true)}
                className="text-sm font-medium text-brand hover:text-brand-hover focus:outline-none"
              >
                Edit Notes
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-500 italic">No notes added yet.</p>
              <button
                onClick={() => setIsEditing(true)}
                className="text-sm font-medium text-brand hover:text-brand-hover focus:outline-none"
              >
                Add Notes
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default TransactionDetails;